'use client'

import LeanMark from '@/components/ui/LeanMark'
import OutletIcon from '@/components/ui/OutletIcon'
import TopicAvatar from '@/components/ui/TopicAvatar'
import { track } from '@/lib/tracking'
import {
  KIND_LABELS,
  MIN_QUERY,
  groupResults,
  orderKinds,
} from '@/services/search/search-client'
import { getBiasLabel, getReliabilityLabel } from '@/utils/bias'
import { useRouter } from 'next/navigation'
import { Fragment, useEffect, useRef, useState } from 'react'

// Search results as the identity mocks draw them (2026-11-01, header A and
// the phone sheet): a row of chips that narrows to one kind (a kind with no
// match is greyed), then the matches grouped by kind, the best-matching kind
// first, then "See all results". Each kind's row says what it is the way the
// rest of the site does: a story with its bar and label, a storyline with
// its chapters, a topic with its picture and what it is, a source with its
// icon and ratings.
//
// useSearch holds the query, the answer, the chosen row and the keyboard, so
// the header's own box (SearchButton) and the panel's box share one state.
// Results arrive as you type; arrows move, Enter opens the chosen row, Enter
// with nothing chosen goes to the full results page. The box and /search
// read the same endpoint, so they always agree.

const PER_GROUP = 3
const PER_KIND = 10
const KIND_ORDER = ['story', 'storyline', 'entity', 'source']

const DAY = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        timeZone: 'America/New_York',
      })
    : ''

export function useSearch({ location, onDone }) {
  const router = useRouter()
  const [query, setQuery] = useState('')
  const [kind, setKind] = useState('all')
  // The last answer and the query it answered. "Loading" is derived: the
  // answer isn't for the current query yet. The old answer stays on screen
  // until the new one lands, so the list doesn't blink on every keystroke.
  const [answer, setAnswer] = useState({ q: '', results: [] })
  const [active, setActive] = useState(-1)
  const lastTracked = useRef('')

  const trimmed = query.trim()
  const live = trimmed.length >= MIN_QUERY
  const loading = live && answer.q !== trimmed

  // Fetch as the reader types; the previous request is cancelled so a slow
  // answer can't overwrite a newer one.
  useEffect(() => {
    if (trimmed.length < MIN_QUERY) return undefined
    const controller = new AbortController()
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(
          `/api/search?q=${encodeURIComponent(trimmed)}&limit=60`,
          { signal: controller.signal }
        )
        const data = res.ok ? await res.json() : { results: [] }
        if (controller.signal.aborted) return
        setAnswer({ q: trimmed, results: data.results || [] })
        setActive(-1)
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Search failed:', err)
      }
    }, 150)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [trimmed])

  // Track a search once the query has held still for a second (2026-10-13),
  // so the no-results list isn't full of prefixes of searches that worked a
  // keystroke later. A click or See all before then records it at once.
  function trackSearch() {
    const a = answer
    if (!a.q || a.q !== trimmed || lastTracked.current === a.q) return
    lastTracked.current = a.q
    track(a.results.length ? 'search_performed' : 'search_no_results', {
      query: a.q,
      result_count: a.results.length,
      location,
    })
  }
  useEffect(() => {
    if (!answer.q || answer.q !== trimmed) return undefined
    const t = setTimeout(trackSearch, 1000)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, trimmed])

  const groups = groupResults(live ? answer.results : [])
  const counts = Object.fromEntries(
    KIND_ORDER.map((k) => [k, groups[k].length])
  )
  // A chosen kind with no matches for the new query falls back to All.
  const view = kind !== 'all' && counts[kind] === 0 ? 'all' : kind
  const shown =
    view === 'all'
      ? orderKinds(groups).flatMap((k) =>
          groups[k].slice(0, PER_GROUP).map((r) => ({ ...r, group: k }))
        )
      : groups[view].slice(0, PER_KIND).map((r) => ({ ...r, group: view }))
  const allHref = `/search?q=${encodeURIComponent(trimmed)}`

  function open(r, position) {
    trackSearch()
    track('search_result_clicked', {
      query: trimmed,
      kind: r.kind,
      position,
      location,
    })
    onDone?.()
    router.push(r.href)
  }

  function seeAll() {
    if (!live) return
    trackSearch()
    track('search_see_all', { query: trimmed, location })
    onDone?.()
    router.push(allHref)
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(shown.length - 1, a + 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(-1, a - 1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (active >= 0 && shown[active]) open(shown[active], active + 1)
      else seeAll()
    }
  }

  function choose(k) {
    setKind(k)
    setActive(-1)
  }

  return {
    query,
    setQuery,
    trimmed,
    live,
    loading,
    total: live ? answer.results.length : 0,
    counts,
    kind: view,
    choose,
    shown,
    active,
    setActive,
    open,
    seeAll,
    onKeyDown,
    allHref,
    empty: live && !loading && answer.results.length === 0,
  }
}

function Chips({ s }) {
  const chip = (id, label, n) => {
    const on = s.kind === id
    const off = n === 0
    return (
      <button
        key={id}
        type="button"
        disabled={off}
        aria-pressed={on}
        onMouseDown={(e) => e.preventDefault()}
        onClick={() => s.choose(id)}
        className={`inline-flex min-h-8 shrink-0 items-center rounded-full border px-3 font-sans text-xs font-semibold transition-colors ${
          on
            ? 'border-foreground bg-foreground text-background'
            : off
              ? 'border-border text-muted-foreground/50'
              : 'border-border text-foreground hover:border-foreground'
        }`}
      >
        {label}
      </button>
    )
  }
  return (
    <div className="flex gap-1.5 overflow-x-auto border-b border-border px-3.5 py-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {chip('all', 'All', s.total)}
      {KIND_ORDER.map((k) => chip(k, KIND_LABELS[k].many, s.counts[k]))}
    </div>
  )
}

function Row({ r }) {
  if (r.kind === 'story')
    return (
      <>
        <span className="min-w-0">
          <span className="block font-serif text-base leading-snug text-foreground">
            {r.title}
          </span>
          <span className="mt-0.5 block font-sans text-xs text-muted-foreground">
            {[DAY(r.date), r.category].filter(Boolean).join(' · ')}
          </span>
        </span>
        {r.coverage_label || r.lean ? (
          <LeanMark node={r} className="w-[92px] shrink-0" />
        ) : (
          <span className="shrink-0 font-sans text-xs text-muted-foreground">
            {r.figure} {r.unit}
          </span>
        )}
      </>
    )
  if (r.kind === 'storyline')
    return (
      <>
        <span className="min-w-0">
          <span className="block font-serif text-base leading-snug text-foreground">
            {r.title}
          </span>
          <span className="mt-0.5 block font-sans text-xs text-muted-foreground">
            {r.figure} {r.unit}
            {r.date ? ` · latest ${DAY(r.date)}` : ''}
          </span>
        </span>
        <span className="shrink-0 font-sans text-xs text-muted-foreground">
          Storyline
        </span>
      </>
    )
  if (r.kind === 'entity')
    return (
      <>
        <span className="flex min-w-0 items-center gap-3">
          <TopicAvatar
            topic={{ name: r.title, image: r.image }}
            size="h-8 w-8 text-[11px]"
          />
          <span className="min-w-0">
            <span className="block font-serif text-base leading-snug text-foreground">
              {r.title}
            </span>
            {r.subtitle && (
              <span className="mt-0.5 block truncate font-sans text-xs text-muted-foreground first-letter:uppercase">
                {r.subtitle}
              </span>
            )}
          </span>
        </span>
        <span className="shrink-0 font-sans text-xs text-muted-foreground">
          {r.figure} {r.unit}
        </span>
      </>
    )
  // A source.
  const lean = typeof r.bias === 'number' ? getBiasLabel(r.bias)?.label : null
  const rel =
    typeof r.reliability === 'number'
      ? getReliabilityLabel(r.reliability)?.label
      : null
  return (
    <>
      <span className="flex min-w-0 items-center gap-3">
        <OutletIcon
          outlet={{ slug: r.slug, name: r.title, bias: r.bias, image: r.image }}
          size="sm"
          className="mx-0.5"
        />
        <span className="min-w-0">
          <span className="block font-serif text-base leading-snug text-foreground">
            {r.title}
          </span>
          {(lean || rel) && (
            <span className="mt-0.5 block font-sans text-xs text-muted-foreground">
              {[lean, rel].filter(Boolean).join(' · ')}
            </span>
          )}
        </span>
      </span>
      <span className="shrink-0 font-sans text-xs text-muted-foreground">
        Outlet
      </span>
    </>
  )
}

// The results: chips, groups, the foot. Nothing until two letters.
export function SearchResults({ s, keysHint = true }) {
  if (!s.live)
    return (
      <p className="px-4 py-5 font-sans text-sm text-muted-foreground">
        Search stories, storylines, topics and outlets.
      </p>
    )
  return (
    <>
      <Chips s={s} />
      {s.shown.length > 0 && (
        <ul role="listbox" aria-label="Results" className="py-1">
          {s.shown.map((r, i) => {
            const first = i === 0 || s.shown[i - 1].group !== r.group
            return (
              <Fragment key={`${r.kind}-${r.slug}`}>
                {first && (
                  <li
                    aria-hidden="true"
                    className="px-4 pb-1 pt-3 font-sans text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                  >
                    {KIND_LABELS[r.group].many}
                  </li>
                )}
                <li
                  role="option"
                  aria-selected={s.active === i}
                  onMouseEnter={() => s.setActive(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => s.open(r, i + 1)}
                  className={`flex cursor-pointer items-center justify-between gap-4 px-4 py-2 ${
                    s.active === i ? 'bg-muted' : ''
                  }`}
                >
                  <Row r={r} />
                </li>
              </Fragment>
            )
          })}
        </ul>
      )}
      {s.empty && (
        <p className="px-4 py-5 font-sans text-sm text-muted-foreground">
          Nothing matches &ldquo;{s.trimmed}&rdquo;. Try a name, a place or a
          different word.
        </p>
      )}
      <div className="flex items-center justify-between gap-4 border-t border-border px-4 py-3 font-sans text-xs text-muted-foreground">
        <button
          type="button"
          onMouseDown={(e) => e.preventDefault()}
          onClick={s.seeAll}
          className="font-semibold text-foreground underline underline-offset-[3px]"
        >
          See all results for &ldquo;{s.trimmed}&rdquo;
        </button>
        {keysHint && (
          <span className="hidden md:inline">
            ↑↓ to move · ↵ to open · esc to close
          </span>
        )}
      </div>
    </>
  )
}
